/* import __COLOCATED_TEMPLATE__ from './customer-authentication.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type NotificationsService from 'embercom/services/notifications-service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type { TaskGenerator } from 'ember-concurrency';
import { get, ajaxDelete } from 'embercom/lib/ajax';

interface ChannelIdentityRule {
  id: number;
  channel: string | null;
  channel_display_name: string;
  audience: string;
  verification_method: string;
  fallback_rule: boolean;
}

interface Column {
  label: string;
  valuePath: string;
  width?: string;
}

interface Signature {
  Element: HTMLDivElement;
  Args: {};
}

export default class CustomerAuthentication extends Component<Signature> {
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked rules: ChannelIdentityRule[] = [];
  @tracked ruleToDelete: ChannelIdentityRule | null = null;
  @tracked showDeleteModal = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadRules).perform();
  }

  get isLoading(): boolean {
    return taskFor(this.loadRules).isRunning;
  }

  @dropTask
  *loadRules(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response: any = yield get(`/ember/channel_identity_rules?app_id=${appId}`);
      this.rules = response.channel_identity_rules || [];
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.channel-identity-rules.notifications.load-error'),
      );
    }
  }

  get columns(): Column[] {
    return [
      {
        label: this.intl.t('new-settings.workspace.channel-identity-rules.table.channel'),
        valuePath: 'channel_display_name',
        width: '30%',
      },
      {
        label: this.intl.t('new-settings.workspace.channel-identity-rules.table.audience'),
        valuePath: 'audience',
        width: '30%',
      },
      {
        label: this.intl.t('new-settings.workspace.channel-identity-rules.table.verification'),
        valuePath: 'verification_method',
        width: '30%',
      },
      {
        label: '',
        valuePath: 'actions',
        width: '10%',
      },
    ];
  }

  get tableData() {
    return this.rules.map((rule) => ({
      ...rule,
      isEditOnly: rule.channel === 'fin_agent_api',
    }));
  }

  @action
  openDeleteModal(rule: ChannelIdentityRule): void {
    this.ruleToDelete = rule;
    this.showDeleteModal = true;
  }

  @action
  closeDeleteModal(): void {
    this.ruleToDelete = null;
    this.showDeleteModal = false;
  }

  @action
  async deleteRule(): Promise<void> {
    if (!this.ruleToDelete) {
      return;
    }

    try {
      let appId = this.appService.app.id;
      await ajaxDelete(`/ember/channel_identity_rules/${this.ruleToDelete.id}?app_id=${appId}`);
      this.rules = this.rules.filter((r) => r.id !== this.ruleToDelete?.id);
      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.workspace.channel-identity-rules.notifications.deleted'),
      );
      this.closeDeleteModal();
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.channel-identity-rules.notifications.delete-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Security::Tabs::CustomerAuthentication': typeof CustomerAuthentication;
    'new-settings/workspace/security/tabs/customer-authentication': typeof CustomerAuthentication;
  }
}
